import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { CartItem } from "@/types";
import { Block, Material } from "@/types/editor/blocks";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number as AUD currency
 * @param amount - The amount to format
 * @returns Formatted price string in AUD
 */
export function formatPrice(amount: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);
}

/**
 * Alternative currency formatter with more options
 * @param amount - The amount to format
 * @param currency - The currency code (default: AUD)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number,
  currency: string = "AUD"
): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a date string or Date object into a localized string
 * @param date - Date to format
 * @param options - Either a format string ('PPP', 'PP', etc.) or Intl.DateTimeFormatOptions
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date,
  options:
    | Intl.DateTimeFormatOptions
    | "PPP"
    | "PP"
    | "P"
    | "p"
    | "ppp"
    | "pp"
    | "relative" = {
    day: "numeric",
    month: "long",
    year: "numeric",
  }
): string {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  // Handle relative time format
  if (options === "relative") {
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - dateObj.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "just now";
    if (diffInMinutes < 60)
      return `${diffInMinutes} minute${diffInMinutes === 1 ? "" : "s"} ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `${diffInHours} hour${diffInHours === 1 ? "" : "s"} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7)
      return `${diffInDays} day${diffInDays === 1 ? "" : "s"} ago`;

    // For older dates, fall back to standard format
    return new Intl.DateTimeFormat("en-AU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(dateObj);
  }

  // Handle format strings
  if (typeof options === "string") {
    switch (options) {
      case "PPP": // March 14, 2024
        return new Intl.DateTimeFormat("en-AU", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }).format(dateObj);
      case "PP": // Mar 14, 2024
        return new Intl.DateTimeFormat("en-AU", {
          year: "numeric",
          month: "short",
          day: "numeric",
        }).format(dateObj);
      case "P": // 03/14/2024
        return new Intl.DateTimeFormat("en-AU", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(dateObj);
      case "p": // 12:00 AM
        return new Intl.DateTimeFormat("en-AU", {
          hour: "numeric",
          minute: "2-digit",
        }).format(dateObj);
      case "ppp": // 12:00:00 AM
        return new Intl.DateTimeFormat("en-AU", {
          hour: "numeric",
          minute: "2-digit",
          second: "2-digit",
        }).format(dateObj);
      case "pp": // 12:00 AM
        return new Intl.DateTimeFormat("en-AU", {
          hour: "numeric",
          minute: "2-digit",
        }).format(dateObj);
      default:
        return new Intl.DateTimeFormat("en-AU").format(dateObj);
    }
  }

  // Handle DateTimeFormatOptions object
  return new Intl.DateTimeFormat("en-AU", options).format(dateObj);
}

/**
 * Converts a brand name to a URL-friendly slug
 * @param brandName - The brand name to convert
 * @returns URL-friendly slug
 */
export function brandNameToSlug(brandName: string): string {
  return brandName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Generates a unique order number in the format AFS-YYYYMMDD-XXXX
 * where XXXX is a random 4-digit number
 */
export async function generateOrderNumber(): Promise<string> {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const random = Math.floor(Math.random() * 10000)
    .toString()
    .padStart(4, "0");
  return `AFS-${year}${month}${day}-${random}`;
}

/**
 * Calculate the lead time for a cart based on the products and quantities
 * @param items Cart items to calculate lead time for
 * @returns Object with lead time in days and breakdown by brand
 */
export function calculateLeadTime(
  items: CartItem[] | (CartItem & { _quantity?: number })[]
) {
  const rockBoardItems: (CartItem & { _quantity?: number })[] = [];
  const xcelr8Items: (CartItem & { _quantity?: number })[] = [];
  const otherItems: (CartItem & { _quantity?: number })[] = [];

  // Categorize items by brand
  items.forEach((item) => {
    const title = item.title?.toLowerCase() || "";
    const partNumber = item.part_number?.toLowerCase() || "";

    if (title.includes("rock board") || partNumber.includes("rbm")) {
      rockBoardItems.push(item as CartItem & { _quantity?: number });
    } else if (title.includes("xcelr8") || partNumber.includes("xl")) {
      xcelr8Items.push(item as CartItem & { _quantity?: number });
    } else {
      otherItems.push(item as CartItem & { _quantity?: number });
    }
  });

  // Calculate lead times for each brand
  let rockBoardLeadTime = 0;
  let xcelr8LeadTime = 0;
  let otherLeadTime = 0;

  // Rock Board lead times
  if (rockBoardItems.length > 0) {
    const totalRockBoardQty = rockBoardItems.reduce(
      (total, item) => total + ("_quantity" in item ? item._quantity || 1 : 1),
      0
    );
    if (totalRockBoardQty > 30) {
      rockBoardLeadTime = 21; // 21 business days for 30+ items
    } else if (totalRockBoardQty > 10) {
      rockBoardLeadTime = 14; // 14 business days for 11-30 items
    } else {
      rockBoardLeadTime = 5; // 5 business days for 1-10 items
    }
  }

  // XCELR8 lead times
  if (xcelr8Items.length > 0) {
    const totalXcelr8Qty = xcelr8Items.reduce(
      (total, item) => total + ("_quantity" in item ? item._quantity || 1 : 1),
      0
    );
    if (totalXcelr8Qty > 10) {
      xcelr8LeadTime = 4; // 3-4 business days for 10+ items, using max value
    } else {
      xcelr8LeadTime = 2; // 1-2 business days for 1-10 items, using max value
    }
  }

  // For other items, use the same lead times as XCELR8
  if (otherItems.length > 0) {
    const totalOtherQty = otherItems.reduce(
      (total, item) => total + ("_quantity" in item ? item._quantity || 1 : 1),
      0
    );
    if (totalOtherQty > 10) {
      otherLeadTime = 4; // 3-4 business days for 10+ items, using max value
    } else {
      otherLeadTime = 2; // 1-2 business days for 1-10 items, using max value
    }
  }

  // Get the maximum lead time
  const maxLeadTime = Math.max(
    rockBoardLeadTime,
    xcelr8LeadTime,
    otherLeadTime
  );

  // Create breakdown information
  const breakdowns = [];
  if (rockBoardLeadTime > 0) {
    const qty = rockBoardItems.reduce(
      (total, item) => total + ("_quantity" in item ? item._quantity || 1 : 1),
      0
    );
    breakdowns.push({
      brand: "Rock Board",
      quantity: qty,
      leadTime: rockBoardLeadTime,
      description: `${rockBoardLeadTime} business days for ${qty} item${qty !== 1 ? "s" : ""}`,
    });
  }

  if (xcelr8LeadTime > 0) {
    const qty = xcelr8Items.reduce(
      (total, item) => total + ("_quantity" in item ? item._quantity || 1 : 1),
      0
    );
    const rangeText = qty > 10 ? "3-4" : "1-2";
    breakdowns.push({
      brand: "XCELR8",
      quantity: qty,
      leadTime: xcelr8LeadTime,
      description: `${rangeText} business days for ${qty} item${qty !== 1 ? "s" : ""}`,
    });
  }

  if (otherLeadTime > 0) {
    const qty = otherItems.reduce(
      (total, item) => total + ("_quantity" in item ? item._quantity || 1 : 1),
      0
    );
    const rangeText = qty > 10 ? "3-4" : "1-2";
    breakdowns.push({
      brand: "Other Products",
      quantity: qty,
      leadTime: otherLeadTime,
      description: `${rangeText} business days for ${qty} item${qty !== 1 ? "s" : ""}`,
    });
  }

  return {
    totalLeadTime: maxLeadTime,
    breakdown: breakdowns,
    note: "*Lead time is in business days and does not include shipping time.",
  };
}

export function generateCartItemId(
  quoteNumber: string,
  partNumber: string
): string {
  // Extract digits from quote number (e.g., "QT-00025" -> "00025")
  const quoteDigits = quoteNumber.replace(/\D/g, "");

  // Get current day of month (1-31) as 2-digit format
  const currentDay = new Date().getDate().toString().padStart(2, "0");

  // Generate random 4-digit number
  const randomDigits = Math.floor(1000 + Math.random() * 9000).toString();

  // Combine all parts to create the unique ID
  return `${quoteDigits}-${partNumber}-${currentDay}-${randomDigits}`;
}

/**
 * Transform product image URLs from webp to jpg format for non-custom products
 * @param imageUrl - The original image URL
 * @param isCustomProduct - Whether the product is a custom product
 * @returns The transformed image URL or original if custom product
 */
export function transformProductImageUrl(
  imageUrl: string,
  isCustomProduct: boolean = false
): string {
  // Don't transform URLs for custom products
  if (isCustomProduct || !imageUrl) {
    return imageUrl;
  }

  // Check if this is a Cloudinary URL with webp format
  if (imageUrl.includes("res.cloudinary.com") && imageUrl.includes(".webp")) {
    // Transform the URL structure:
    // From: https://res.cloudinary.com/djowccubk/image/upload/v1739754162/products/AFS_Rock_Board_RBMT3_Magnetic.webp
    // To: https://res.cloudinary.com/djowccubk/image/upload/v1748923487/products/jpg/AFS_Rock_Board_RBMT3_Magnetic.jpg

    return imageUrl
      .replace(/\/v\d+\/products\//, "/v1748923487/products/jpg/")
      .replace(/\.webp$/, ".jpg");
  }

  return imageUrl;
}

/**
 * Generates a Hi-Vis part number based on design block structure
 * Format: DCL-{block0.width}-{block0.height}-{block0.materialCode}-{block0.ref}-{block1.ref}[R]
 * @param design - The design object containing blocks
 * @param materials - Array of materials to match colors with refs and material codes
 * @returns Generated part number string with optional "R" suffix for rounded corners and "S" prefix for stacked containers
 */
export function hiVisPartNumberGenerator(
  design: {
    blocks: Block[];
  },
  materials: Material[]
): string {
  if (!design.blocks || design.blocks.length === 0) {
    return "DCL-0-0-0000";
  }

  // Get the first block for dimensions and base material
  const block0 = design.blocks[0];
  if (!block0) {
    return "DCL-0-0-0000";
  }

  const width = Math.round(block0.width || 0);
  const height = Math.round(block0.height || 0);

  // Find base material from block0 color
  const baseMaterial = materials.find((m) => m.color === block0.color);
  const materialCode = baseMaterial?.materialCode || "0000";

  // Generate refs starting with block0's material ref
  const refs: string[] = [];
  const containerDimensions: Array<{ width: number; height: number }> = [];
  let hasRoundedCorners = false;

  // Check if block0 has rounded corners
  if (block0.cornerRadius && block0.cornerRadius > 0) {
    hasRoundedCorners = true;
  }

  // Add block0's material ref and dimensions
  refs.push(baseMaterial?.ref || "X");
  containerDimensions.push({ width: block0.width, height: block0.height });

  // Recursive function to traverse all container levels
  const traverseContainers = (blocks: Block[], level: number = 1) => {
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];

      // Skip character containers (blocks that directly contain character blocks)
      if (block.blocks && block.blocks.length > 0) {
        const isCharacterContainer = block.blocks.every(
          (child: Block) => child.character !== undefined
        );

        if (isCharacterContainer) {
          continue; // Skip this block as it's a character container
        }

        // This is a valid container - add it to refs
        // Check if this block has rounded corners
        if (block.cornerRadius && block.cornerRadius > 0) {
          hasRoundedCorners = true;
        }

        // Add this block's material ref and dimensions
        const blockColor = block.color;
        let ref = "X";
        if (blockColor === "#000000" || blockColor === "#000") {
          ref = "B";
        } else {
          const material = materials.find((m) => m.color === blockColor);
          ref = material?.ref || "X";
        }

        // Check if this container has stacked elements (verticalStack)
        const isStacked = block.verticalStack === true;
        if (isStacked) {
          ref = "S" + ref;
        }

        refs.push(ref);

        containerDimensions.push({
          width: block.width,
          height: block.height,
        });

        // Recursively traverse this container's children
        traverseContainers(block.blocks, level + 1);
      }
    }
  };

  // Start traversing from block0's children
  if (block0.blocks && block0.blocks.length > 0) {
    traverseContainers(block0.blocks);
  } else {
    console.log("❌ Block0 has no children to traverse");
  }

  // Group refs by their dimensions
  const dimensionGroups: Array<{ dimensions: string; refs: string[] }> = [];

  for (let i = 0; i < refs.length; i++) {
    const ref = refs[i];
    const dimension = containerDimensions[i];
    const dimensionKey = `${dimension.width}x${dimension.height}`;

    // Find existing group or create new one
    let group = dimensionGroups.find((g) => g.dimensions === dimensionKey);
    if (!group) {
      group = { dimensions: dimensionKey, refs: [] };
      dimensionGroups.push(group);
    }
    group.refs.push(ref);
  }

  // Join refs within same dimension groups (no hyphens), separate different groups with hyphens
  const groupStrings = dimensionGroups.map((group) => group.refs.join(""));
  const refString = groupStrings.join("-");

  // Add "R" suffix if any container has rounded corners
  const roundedSuffix = hasRoundedCorners ? "R" : "";

  const partNumber = `DCL-${width}-${height}-${materialCode}-${refString}${roundedSuffix}`;

  return partNumber;
}

/**
 * Formats file size in bytes to a human-readable string
 * @param bytes - The file size in bytes
 * @returns Formatted file size string (e.g., "10.1 KB", "2.5 MB")
 */
export function formatFileSize(bytes: number | string): string {
  const numBytes = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;

  if (isNaN(numBytes) || numBytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(numBytes) / Math.log(k));

  return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Generates a placeholder decal number based on character limits
 * @param maxChars - Maximum number of characters allowed
 * @returns Formatted placeholder string
 */
export function generateDecalPlaceholder(maxChars?: number): string {
  if (!maxChars) return "AFS-01"; // Default fallback
  
  switch (maxChars) {
    case 2:
      return "01";
    case 3:
      return "AFS";
    case 4:
      return "AFS1";
    case 5:
      return "AFS01";
    default: // 6 or more characters
      return "AFS-01";
  }
}
